import React, { useContext, useMemo, useState } from 'react';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import styled from 'styled-components';

import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';

import { AgentKbContext } from './AgentKbProvider';
import ArtefactEditor from './ArtefactEditor';
import { buildKbFileTree, firstFileId, KbTreeNode } from './buildKbFileTree';

// Browse the current KB as a filesystem: a directory + file tree on the left,
// the selected file's markdown on the right. Replaces the old left nav sidebar —
// everything comes from AgentKbContext, no server call.

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px 0 24px;
`;

const BodyRow = styled.div`
  display: flex;
  height: 70vh;
  min-height: 0;
`;

const TreePane = styled.div`
  width: 300px;
  flex-shrink: 0;
  border-right: 1px solid var(--color-border-secondary);
  overflow-y: auto;
  padding: 12px 0;
`;

const ContentPane = styled.div`
  flex: 1;
  min-width: 0;
  overflow-y: auto;
`;

const EmptyDiv = styled.div`
  padding: 24px 32px;
`;

const RowButton = styled.button<{ isSelected: boolean; depth: number }>`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  text-align: left;
  border: 0;
  cursor: pointer;
  padding: 6px 16px;
  padding-left: ${(props) => 16 + props.depth * 12}px;
  background-color: ${(props) =>
    props.isSelected ? 'var(--color-bg-secondary)' : 'transparent'};
`;

const CaretSpan = styled.span`
  display: inline-block;
  width: 12px;
  color: var(--color-text-secondary);
`;

const TreeNodes = ({
  nodes,
  depth,
  selectedId,
  onSelect,
}: {
  nodes: KbTreeNode[];
  depth: number;
  selectedId: string | null;
  onSelect: (artefactId: string) => void;
}) => (
  <>
    {nodes.map((node) => {
      if (node.type === 'file') {
        return (
          <RowButton
            key={`file:${node.artefactId}`}
            type="button"
            depth={depth}
            isSelected={node.artefactId === selectedId}
            data-testid={`agent-kb-browser-file-${node.artefactId}`}
            onClick={() => onSelect(node.artefactId)}
          >
            <CaretSpan />
            <Typography.Body type="Body 13">{node.name}</Typography.Body>
          </RowButton>
        );
      }
      return (
        <DirNode
          key={`dir:${depth}:${node.name}`}
          node={node}
          depth={depth}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      );
    })}
  </>
);

const DirNode = ({
  node,
  depth,
  selectedId,
  onSelect,
}: {
  node: Extract<KbTreeNode, { type: 'dir' }>;
  depth: number;
  selectedId: string | null;
  onSelect: (artefactId: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  return (
    <>
      <RowButton
        type="button"
        depth={depth}
        isSelected={false}
        onClick={() => setIsOpen((open) => !open)}
      >
        <CaretSpan>{isOpen ? '▾' : '▸'}</CaretSpan>
        <Typography.Body type="Body 13 Bold">{node.name}</Typography.Body>
      </RowButton>
      {isOpen && (
        <TreeNodes
          nodes={node.children}
          depth={depth + 1}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      )}
    </>
  );
};

const KbBrowserDialog = ({ close }: { close: () => void }) => {
  const { artefacts } = useContext(AgentKbContext);
  const tree = useMemo(() => buildKbFileTree(artefacts), [artefacts]);
  const [selectedId, setSelectedId] = useState<string | null>(() =>
    firstFileId(tree),
  );

  const selected = artefacts.find((artefact) => artefact.id === selectedId);

  return (
    <ModalTransition>
      <Modal width="x-large" onClose={close} shouldScrollInViewport>
        <HeaderRow>
          <Typography.Header type="H4">Knowledge base</Typography.Header>
          <Button type="Ghost" size="Small" label="Close" onClick={close} />
        </HeaderRow>
        <BodyRow data-testid="agent-kb-browser">
          <TreePane>
            <TreeNodes
              nodes={tree}
              depth={0}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
          </TreePane>
          <ContentPane>
            {selected ? (
              <ArtefactEditor artefact={selected} />
            ) : (
              <EmptyDiv>
                <Typography.Body
                  type="Body 13"
                  color="var(--color-text-secondary)"
                >
                  Select a file to view its contents.
                </Typography.Body>
              </EmptyDiv>
            )}
          </ContentPane>
        </BodyRow>
      </Modal>
    </ModalTransition>
  );
};

export default KbBrowserDialog;
